import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon, TrendingUp, Zap, BellRing, Check, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fadeUp, stagger, viewportOnce, EASE } from "@/lib/motion";

// Same-origin `/api` — Emergent's ingress routes `/api/*` to the backend on
// every frontend host (preview, .emergent.host, custom domain), so no
// `REACT_APP_BACKEND_URL` swap is needed and CORS never applies.
const API = "/api";

const CHAPTERS = [
  {
    n: "01",
    icon: BellRing,
    title: "A cancellation hits.",
    body: "Someone drops out at 2pm. That chair used to sit empty for the rest of the day — pure lost revenue.",
  },
  {
    n: "02",
    icon: Zap,
    title: "Book Away lights it up.",
    body: "The open slot goes live instantly to nearby clients hunting for last-minute appointments in your category.",
  },
  {
    n: "03",
    icon: TrendingUp,
    title: "The chair fills. You earn.",
    body: "A new client books in minutes. No phone tag, no discounts to your regulars — just recovered income.",
  },
];

const BUSINESS_TYPES = ["Hair", "Barber", "Brows & Lashes", "Makeup", "Skin & Injectables", "Spray Tans"];

function WaitlistForm() {
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    business_type: "",
  });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email || !form.business_type) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        ...form,
        city: "Sydney",
        go_live_date: date ? format(date, "yyyy-MM-dd") : null,
      });
      setDone(true);
      toast.success("You're on the list. We'll be in touch shortly.");
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("This email is already on the waitlist.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="waitlist-success"
        className="flex flex-col items-start gap-4 rounded-2xl border border-gold/30 bg-[#EDEAE4] p-10"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gold text-black">
          <Check className="h-6 w-6" />
        </div>
        <h3 className="font-serif text-3xl text-[#2A2724]">Welcome aboard.</h3>
        <p className="text-[#6B6B6B]">
          Your spot on the Book Away partner waitlist is secured. Watch your inbox — early partners get priority onboarding and zero platform fees for the first 90 days.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="waitlist-form"
      className="rounded-2xl border border-black/10 bg-[#EDEAE4] p-6 md:p-8"
    >
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-2">
        <p className="overline !mb-0">Join the partner waitlist</p>
        <span
          data-testid="waitlist-city-badge"
          className="inline-flex items-center gap-1.5 rounded-full border border-gold/60 bg-gold/10 px-3 py-1 text-[0.62rem] font-medium uppercase tracking-[0.24em] text-[#8B6F1F]"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-gold" />
          Sydney businesses only
        </span>
      </div>
      <p className="mb-6 text-sm leading-relaxed text-[#6B6B6B]">
        Book Away launches in <span className="text-[#2A2724]">Sydney</span> first.
        This form is for Sydney salons, barbers and studios claiming a
        Foundation Member spot. Based elsewhere?{" "}
        <a href="#expansion" className="text-gold underline-offset-4 hover:underline">
          Join your city's waitlist here
        </a>
        .
      </p>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="business_name" className="text-xs text-[#6B6B6B]">Business name</Label>
          <Input
            id="business_name"
            data-testid="waitlist-business-name"
            value={form.business_name}
            onChange={(e) => update("business_name", e.target.value)}
            placeholder="Maison Noir"
            className="border-black/10 bg-[#F7F5F2] text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact_name" className="text-xs text-[#6B6B6B]">Your name</Label>
          <Input
            id="contact_name"
            data-testid="waitlist-contact-name"
            value={form.contact_name}
            onChange={(e) => update("contact_name", e.target.value)}
            placeholder="Alex Rivera"
            className="border-black/10 bg-[#F7F5F2] text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs text-[#6B6B6B]">Work email</Label>
          <Input
            id="email"
            type="email"
            data-testid="waitlist-email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@studio.com"
            className="border-black/10 bg-[#F7F5F2] text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs text-[#6B6B6B]">Business type</Label>
          <Select value={form.business_type} onValueChange={(v) => update("business_type", v)}>
            <SelectTrigger
              data-testid="waitlist-business-type"
              className="border-black/10 bg-[#F7F5F2] text-[#2A2724] focus:ring-gold"
            >
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent className="border-black/10 bg-[#EDEAE4] text-[#2A2724]">
              {BUSINESS_TYPES.map((t) => (
                <SelectItem key={t} value={t} className="focus:bg-black/5 focus:text-gold">
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label className="text-xs text-[#6B6B6B]">Preferred go-live date (optional)</Label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="waitlist-date-trigger"
                className="flex w-full items-center gap-3 rounded-md border border-black/10 bg-[#F7F5F2] px-3 py-2.5 text-left text-sm text-[#2A2724] transition-colors duration-300 hover:border-gold/40"
              >
                <CalendarIcon className="h-4 w-4 text-gold" />
                {date ? format(date, "PPP") : <span className="text-[#9a9a9a]">Pick a date</span>}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto border-black/10 bg-[#EDEAE4] p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
                className="text-[#2A2724]"
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <Button
        type="submit"
        data-testid="waitlist-submit"
        disabled={loading}
        className="mt-6 w-full rounded-full bg-gold py-6 text-sm font-medium text-black hover:bg-[#AA8627] transition-colors duration-300"
      >
        {loading ? "Securing your spot…" : "Claim early access"}
      </Button>
    </form>
  );
}

export default function Businesses() {
  const navigate = useNavigate();
  return (
    <section
      id="businesses"
      data-testid="businesses-section"
      className="relative z-10 bg-[#F7F5F2] px-6 py-28 md:px-12 md:py-40"
    >
      <div className="mx-auto max-w-[1600px]">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="overline mb-6">For Businesses</p>
            <motion.h2
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#2A2724]"
            >
              Turn your empty appointment slots into{" "}
              <span className="italic text-gold">revenue.</span>
            </motion.h2>
            <motion.p
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              className="mt-8 max-w-md text-lg leading-relaxed text-[#6B6B6B]"
            >
              Last-minute cancellations don't have to mean lost income. Book Away
              instantly surfaces your open chairs to nearby clients ready to book
              now — so an empty slot becomes a paying customer in minutes.
            </motion.p>

            <motion.button
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              data-testid="businesses-join-cta"
              onClick={() => navigate("/join-network")}
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-medium text-black transition-colors duration-300 hover:bg-[#AA8627]"
            >
              Join the Network to View Membership Options
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </motion.button>
          </div>

          <div className="lg:col-span-7 lg:pl-12">
            <motion.div
              variants={stagger(0.15)}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              className="flex flex-col"
            >
              {CHAPTERS.map((c, i) => (
                <motion.div
                  key={c.n}
                  variants={fadeUp}
                  className={`group flex items-start gap-6 py-8 md:gap-10 ${
                    i !== CHAPTERS.length - 1 ? "border-b border-black/10" : ""
                  }`}
                >
                  <span className="font-serif text-4xl md:text-5xl font-light text-black/10 transition-colors duration-500 group-hover:text-gold/60">
                    {c.n}
                  </span>
                  <div className="flex-1">
                    <div className="mb-3 flex items-center gap-3">
                      <c.icon className="h-5 w-5 text-gold" />
                      <h3 className="font-serif text-2xl md:text-3xl text-[#2A2724]">
                        {c.title}
                      </h3>
                    </div>
                    <p className="max-w-lg text-[#6B6B6B] leading-relaxed">
                      {c.body}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center"
        >
          <div className="lg:col-span-5">
            <div className="grid grid-cols-3 gap-6">
              {[
                { k: "+31%", v: "avg. monthly revenue recovered" },
                { k: "4 min", v: "median time to fill a slot" },
                { k: "0", v: "phone calls required" },
              ].map((s) => (
                <div key={s.v}>
                  <p className="font-serif text-3xl md:text-4xl text-gold">{s.k}</p>
                  <p className="mt-2 text-xs leading-relaxed text-[#6B6B6B]">{s.v}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <WaitlistForm />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
